import { StoreGroup, Store, PaginationParams, StoreGroupFilters, Planogram } from '../types/storeGroup';
import { ApiResponse, PaginatedResponse } from '../types/index';
import { createSuccessResponse, createErrorResponse, createPaginatedResponse } from '../utils/responseTransformer';
import httpClient from './httpClient';

// 模拟数据
import { mockStoreGroups, mockStores } from '../mock/storeGroupData.js';
import { mockPlanograms } from '../mock/planogramData.js';
import { API_ENDPOINTS } from '../utils/apiEndpoints';
import { warehouseService } from './warehouseService';
import { WHOLE_USE_MOCK } from './api';

/**
 * Service class for Store Group operations
 */
export class StoreGroupService {
  private static instance: StoreGroupService;
  private storeGroups: StoreGroup[] = [];
  private stores: Store[] = [];
  private planograms: Planogram[] = [];
  
  // 静态配置，控制是否使用 mock 数据
  private static useMock: boolean = true;

  /**
   * Private constructor to enforce singleton pattern
   */
  private constructor() {
    // Initialize with mock data
    this.storeGroups = [...mockStoreGroups];
    this.stores = [...mockStores];
    this.planograms = [...mockPlanograms];
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): StoreGroupService {
    if (!StoreGroupService.instance) {
      StoreGroupService.instance = new StoreGroupService();
    }
    return StoreGroupService.instance;
  }
  
  /**
   * 设置是否使用 mock 数据
   * @param useMock 是否使用 mock 数据
   */
  public static setUseMock(useMock: boolean): void {
    StoreGroupService.useMock = useMock;
  }
  
  /**
   * 获取当前是否使用 mock 数据
   */
  public static getUseMock(): boolean {
    return StoreGroupService.useMock;
  }

  /**
   * Simulate API delay
   * @param ms Milliseconds to delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get Store Groups with filtering, pagination and sorting
   */
  public async getStoreGroups(
    filters: StoreGroupFilters = {},
    pagination: PaginationParams = { page: 1, pageSize: 10 }
  ): Promise<ApiResponse<PaginatedResponse<StoreGroup>>> {
    try {
      if (StoreGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        // Apply filters
        let filteredGroups = [...this.storeGroups];

        // Filter by status
        if (filters.status && filters.status !== 'all') {
          filteredGroups = filteredGroups.filter(group => group.status === filters.status);
        }

        // Filter by display group code
        if (filters.displayGroupCode) {
          filteredGroups = filteredGroups.filter(group => group.displayGroupCode === filters.displayGroupCode);
        }

        // Filter by search term (code or name)
        if (filters.search) {
          const searchTerm = filters.search.toLowerCase();
          filteredGroups = filteredGroups.filter(
            group =>
              group.code.toLowerCase().includes(searchTerm) ||
              group.name.toLowerCase().includes(searchTerm)
          );
        }

        // Filter by division code
        if (filters.mainDivisionCode) {
          filteredGroups = filteredGroups.filter(group => (group as any).mainDivisionCode === filters.mainDivisionCode);
        }

        // Apply sorting
        if (pagination.sortBy) {
          filteredGroups.sort((a, b) => {
            const sortField = pagination.sortBy as keyof StoreGroup;
            let valueA: any = sortField ? a[sortField] : null;
            let valueB: any = sortField ? b[sortField] : null;

            // Handle date sorting
            if (sortField === 'gmtCreate' || sortField === 'lastExportTime' || sortField === 'lastImportTime') {
              valueA = valueA ? new Date(valueA).getTime() : 0;
              valueB = valueB ? new Date(valueB).getTime() : 0;
            }

            // Apply sort order
            if (pagination.sortOrder === 'asc') {
              return valueA > valueB ? 1 : -1;
            } else {
              return valueA < valueB ? 1 : -1;
            }
          });
        } else {
          // Default sort by gmtCreate desc
          filteredGroups.sort((a, b) => {
            return new Date(b.gmtCreate).getTime() - new Date(a.gmtCreate).getTime();
          });
        }

        // Apply pagination
        const total = filteredGroups.length;
        const startIndex = (pagination.page - 1) * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        const paginatedGroups = filteredGroups.slice(startIndex, endIndex);

        return createPaginatedResponse(paginatedGroups, pagination.page, pagination.pageSize, total);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.STORE_GROUP.PAGE, { ...filters, ...pagination } );
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching store groups:', error);
      return createErrorResponse(error.message);
    }
  }

    async listStoreGroup(filters): Promise<ApiResponse<StoreGroup[]>> {
     if (StoreGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        // Apply filters
        let filteredGroups = [...this.storeGroups];

        // Filter by status
        if (filters.status && filters.status !== 'all') {
          filteredGroups = filteredGroups.filter(group => group.status === filters.status);
        }

        // Filter by display group code
        if (filters.displayGroupCode) {
          filteredGroups = filteredGroups.filter(group => group.displayGroupCode === filters.displayGroupCode);
        }

        // Filter by search term (code or name)
        if (filters.search) {
          const searchTerm = filters.search.toLowerCase();
          filteredGroups = filteredGroups.filter(
            group =>
              group.code.toLowerCase().includes(searchTerm) ||
              group.name.toLowerCase().includes(searchTerm)
          );
        }

        // Filter by division code
        if (filters.mainDivisionCode) {
          filteredGroups = filteredGroups.filter(group => (group as any).mainDivisionCode === filters.mainDivisionCode);
        }

        return createSuccessResponse(filteredGroups);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.STORE_GROUP.MULTI_QUERY, { ...filters} );
        return response.data;
      }
    }

  /**
   * Get Store Group by Code
   */
  public async getStoreGroupByCode(code: string): Promise<ApiResponse<StoreGroup>> {
    try {
      if (StoreGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        const storeGroup = this.storeGroups.find(group => group.code === code);

        if (!storeGroup) {
          return createErrorResponse('门店组不存在', 'NOT_FOUND');
        }

        return createSuccessResponse(storeGroup);
      } else {
        // 调用真实 API
        const response = await httpClient.get(API_ENDPOINTS.STORE_GROUP.QUERY_BY_CODE + '?code=' + code);
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching store group details by code:', error);
      return createErrorResponse(error.message);
    }
  }

  /**
   * Get Store Group by Code
   */
  public async getStoresByGroupsIds(groupIds: string[]): Promise<ApiResponse<StoreGroup>> {
    try {
      if (StoreGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        const storeGroup = this.storeGroups.find(group => groupIds.includes(group.id));

        if (!storeGroup) {
          return createErrorResponse('门店组不存在', 'NOT_FOUND');
        }

        return createSuccessResponse(storeGroup);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.STORE_GROUP.STORE_GROUP_STORES, {ids: groupIds});
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching store group details by code:', error);
      return createErrorResponse('获取门店组详情失败');
    }
  }

  /**
   * Get stores by store codes
   */
  public async getStoresByCodes(storeCodes: string[]): Promise<ApiResponse<Store[]>> {
    try {
      if (StoreGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);
        
        const stores = this.stores.filter(store => storeCodes.includes(store.code));
        return createSuccessResponse(stores);
      } else {
        // Call real API
        // 使用warehouseService.listWarehouse方法获取所有门店，然后过滤出属于当前门店组的门店
        return warehouseService.listWarehouse({});
      }
    } catch (error) {
      console.error('Error fetching stores by codes:', error);
      return createErrorResponse('获取门店列表失败');
    }
  }

  /**
   * Create new Store Group
   */
  public async createStoreGroup(storeGroup: StoreGroup): Promise<ApiResponse<StoreGroup>> {
    try {
      if (StoreGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        // Check if code already exists
        const existingGroup = this.storeGroups.find(group => group.code === storeGroup.code);
        if (existingGroup) {
          return createErrorResponse('门店组编码已存在', 'DUPLICATE_CODE');
        }

        // Set creation time
        const now = Date.now();
        const newStoreGroup: StoreGroup = {
          ...storeGroup,
          id: `sg-${Date.now()}`, // Generate a unique ID
          gmtCreate: now,
        };

        // Add to collection
        this.storeGroups.push(newStoreGroup);

        return createSuccessResponse(newStoreGroup);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.STORE_GROUP.CREATE, storeGroup);
        return response.data;
      }
    } catch (error) {
      console.error('Error creating store group:', error);
      return createErrorResponse(error.message);
    }
  }

  /**
   * Update existing Store Group
   */
  public async updateStoreGroup(id: string, storeGroup: Partial<StoreGroup>): Promise<ApiResponse<StoreGroup>> {
    try {
      if (StoreGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        const index = this.storeGroups.findIndex(group => group.code === id);
        if (index === -1) {
          return createErrorResponse('门店组不存在', 'NOT_FOUND');
        }

        // Update the store group
        const updatedStoreGroup: StoreGroup = {
          ...this.storeGroups[index],
          ...storeGroup,
        };

        this.storeGroups[index] = updatedStoreGroup;

        return createSuccessResponse(updatedStoreGroup);
      } else {
        const response = await httpClient.post(API_ENDPOINTS.STORE_GROUP.UPDATE, storeGroup);
        return response.data;
      }
    } catch (error) {
      console.error('Error updating store group:', error);
      return createErrorResponse(error.message);
    }
  }

  /**
   * Batch update status (activate/deactivate)
   */
  public async batchUpdateStatus(ids: string[], status: 'ENABLE' | 'DISABLE'): Promise<ApiResponse<{ success: number, skipped: number }>> {
    try {
      if (StoreGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        let successCount = 0;
        let skippedCount = 0;

        for (const id of ids) {
          const index = this.storeGroups.findIndex(group => group.id === id);
          if (index === -1) {
            skippedCount++;
            continue;
          }

          // Update status
          this.storeGroups[index] = {
            ...this.storeGroups[index],
            status,
          };

          successCount++;
        }

        return createSuccessResponse({ success: successCount, skipped: skippedCount });
      } else {
        // 调用真实 API
        if (status === 'ENABLE') {
          const response = await httpClient.post(API_ENDPOINTS.STORE_GROUP.BATCH_ACTIVE, {
            ids
          });
          return response.data;
        } else {
          const response = await httpClient.post(API_ENDPOINTS.STORE_GROUP.BATCH_DEACTIVE, {
            ids
          });
          return response.data;
        }
      }
    } catch (error) {
      console.error('Error batch updating status:', error);
      return createErrorResponse(error.message);
    }
  }

  /**
   * Export data to Nielsen
   */
  public async exportToNielsen(storeGroupId: string): Promise<ApiResponse<{ fileName: string, downloadUrl: string }>> {
    try {
      if (StoreGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        const storeGroup = this.storeGroups.find(group => group.id === storeGroupId);
        if (!storeGroup) {
          return createErrorResponse('门店组不存在', 'NOT_FOUND');
        }

        // Update last export time
        const now = Date.now();
        const nowString = new Date().toISOString().replace('T', ' ').substring(0, 19);
        const index = this.storeGroups.findIndex(group => group.id === storeGroupId);
        this.storeGroups[index] = {
          ...storeGroup,
          lastExportTime: now,
        };

        // Generate file name
        const fileName = `StoreGroup_${storeGroup.code}_DisplayGroup_${storeGroup.displayGroupCode}_Date_${nowString.replace(/[\s:-]/g, '')}.xlsx`;

        return createSuccessResponse({
          fileName,
          downloadUrl: `/api/v1/downloads/${fileName}`,
        });
      } else {
        // 调用真实 API
        const response = await httpClient.get(API_ENDPOINTS.STORE_GROUP.EXPORT_ITEM_DATA + "?code=" + storeGroupId);
        return response.data;
      }
    } catch (error) {
      console.error('Error exporting to Nielsen:', error);
      return createErrorResponse('导出数据失败');
    }
  }

  /**
   * Import benchmark planogram from Nielsen
   */
  public async importBenchmark(storeGroupId: string, file: File): Promise<ApiResponse<Planogram>> {
    if (StoreGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        const storeGroup = this.storeGroups.find(group => group.id === storeGroupId);
        if (!storeGroup) {
          return createErrorResponse('门店组不存在', 'NOT_FOUND');
        }

        // Update last import time
        const now = Date.now();
        // 使用当前最新时间确保新导入的数据排在最上面
        const currentDateTime = new Date();
        const nowString = currentDateTime.toISOString().replace('T', ' ').substring(0, 19);
        const index = this.storeGroups.findIndex(group => group.id === storeGroupId);
        this.storeGroups[index] = {
          ...storeGroup,
          lastImportTime: now,
        };

        // Create a mock planogram with latest timestamp
        const planogram: Planogram = {
          id: `pg-${now}`,
          planogramId: `POG-${currentDateTime.toISOString().substring(2, 10).replace(/-/g, '')}-${String(Math.floor(Math.random() * 900) + 100).padStart(3, '0')}`,
          code: `PG-${storeGroupId.substring(3)}-${now.toString().substring(8)}`,
          name: `Imported Planogram from ${file.name}`,
          storeGroupId,
          displayGroupCode: storeGroup.displayGroupCode,
          layoutName: `Layout from ${file.name}`,
          width: 120,
          height: 80,
          shelfCount: 2,
          productCount: 7,
          importTime: nowString,
          importedBy: 'current_user',
          fileName: file.name,
          status: '草稿',
          isMainVersion: false,
          layout: {
            shelves: [
              {
                id: 'shelf-1',
                position: 1,
                height: 40,
                width: 120,
                remainingSpace: 20,
                products: [
                  {
                    id: 'prod-1',
                    code: 'P001',
                    name: '可口可乐',
                    width: 20,
                    position: 1,
                    facings: 2,
                    inventory: 120,
                    dailySales: 15,
                    suggestedFacings: 2,
                    affectedStores: 8,
                  },
                  {
                    id: 'prod-2',
                    code: 'P002',
                    name: '百事可乐',
                    width: 20,
                    position: 3,
                    facings: 2,
                    inventory: 100,
                    dailySales: 12,
                    suggestedFacings: 2,
                    affectedStores: 8,
                  },
                  {
                    id: 'prod-3',
                    code: 'P003',
                    name: '雪碧',
                    width: 20,
                    position: 5,
                    facings: 2,
                    inventory: 80,
                    dailySales: 10,
                    suggestedFacings: 1,
                    affectedStores: 8,
                  },
                  {
                    id: 'prod-4',
                    code: 'P004',
                    name: '芬达',
                    width: 20,
                    position: 7,
                    facings: 2,
                    inventory: 60,
                    dailySales: 8,
                    suggestedFacings: 1,
                    affectedStores: 8,
                  },
                ],
              },
              {
                id: 'shelf-2',
                position: 2,
                height: 40,
                width: 120,
                remainingSpace: 40,
                products: [
                  {
                    id: 'prod-5',
                    code: 'P005',
                    name: '农夫山泉',
                    width: 15,
                    position: 1,
                    facings: 2,
                    inventory: 150,
                    dailySales: 20,
                    suggestedFacings: 3,
                    affectedStores: 8,
                  },
                  {
                    id: 'prod-6',
                    code: 'P006',
                    name: '康师傅矿泉水',
                    width: 15,
                    position: 3,
                    facings: 2,
                    inventory: 120,
                    dailySales: 18,
                    suggestedFacings: 2,
                    affectedStores: 8,
                  },
                  {
                    id: 'prod-7',
                    code: 'P007',
                    name: '怡宝矿泉水',
                    width: 15,
                    position: 5,
                    facings: 2,
                    inventory: 100,
                    dailySales: 15,
                    suggestedFacings: 2,
                    affectedStores: 8,
                  },
                ],
              },
            ],
            width: 120,
            height: 80,
          },
          createdAt: nowString,
          updatedAt: nowString,
        };

        // Add to planograms collection
        this.planograms.push(planogram);

        return createSuccessResponse(planogram);
      } else {
        // 调用真实 API
        const formData = new FormData();
        formData.append('file', file);

        const response = await httpClient.post(
          API_ENDPOINTS.STORE_GROUP.IMPORT_NIELSEN_DATA + "?storeGroupId=" + storeGroupId,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        return response.data;
      }
  }

  public async exportPlanogramBenchmark(planogramId: string): Promise<ApiResponse<{ fileName: string, downloadUrl: string }>> {
    try {
      if (StoreGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        
      } else {
        // 调用真实 API
        const response = await httpClient.get(API_ENDPOINTS.STORE_GROUP.EXPORT_NIELSEN_DATA + "?planogramId=" + planogramId);
        return response.data;
      }
    } catch (error) {
      console.error('Error exporting to Nielsen:', error);
      return createErrorResponse(error.message);
    }
  }

  /**
   * Get planogram by store group ID
   */
  public async getPlanogram(storeGroupId: string): Promise<ApiResponse<Planogram>> {
    try {
      if (StoreGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        const planogram = this.planograms.find(p => p.storeGroupId === storeGroupId);

        if (!planogram) {
          return createErrorResponse('该门店组没有关联的陈列图', 'NOT_FOUND');
        }

        return createSuccessResponse(planogram);
      } else {
        // 调用真实 API
        const response = await httpClient.get(API_ENDPOINTS.STORE_GROUP.QUERY_BENCHMARK_DATA + "?storeGroupId=" + storeGroupId);
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching planogram:', error);
      return createErrorResponse(error.message);
    }
  }

  /**
   * Update planogram
   */
  public async updatePlanogram(planogram: Planogram): Promise<ApiResponse<Planogram>> {
    try {
      if (StoreGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        const index = this.planograms.findIndex(p => p.id === planogram.id);
        if (index === -1) {
          return createErrorResponse('陈列图不存在', 'NOT_FOUND');
        }

        // Update planogram
        this.planograms[index] = planogram;

        return createSuccessResponse(planogram);
      } else {
        // 调用真实 API
        const response = null;
        return response.data;
      }
    } catch (error) {
      console.error('Error updating planogram:', error);
      return createErrorResponse(error.message);
    }
  }

  /**
   * Get planograms for a store group
   */
  public async getPlanograms(storeGroupId: string): Promise<ApiResponse<Planogram[]>> {
    try {
      if (StoreGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);
        
        const planograms = this.planograms.filter(p => p.storeGroupId === storeGroupId);
        return createSuccessResponse(planograms);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.STORE_GROUP.PAGE_BENCHMARK_DATA, {"storeGroupCode":storeGroupId});
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching planograms:', error);
      return createErrorResponse(error.message);
    }
  }

  /**
   * Submit planogram for review
   */
  public async submitPlanogramForReview(planogramId: string): Promise<ApiResponse<Planogram>> {
    try {
      if (StoreGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(500);

        const index = this.planograms.findIndex(p => p.id === planogramId);
        if (index === -1) {
          return createErrorResponse('陈列图不存在', 'NOT_FOUND');
        }

        // Update status to PendingReview
        this.planograms[index] = {
          ...this.planograms[index],
          status: '待审核',
          submittedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        return createSuccessResponse(this.planograms[index]);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.STORE_GROUP.PLANOGRAM_SUBMIT_APPROVE, {id: planogramId});
        return response.data;
      }
    } catch (error) {
      console.error('Error submitting planogram for review:', error);
      return createErrorResponse(error.message);
    }
  }

  public async setAsMainVersion(planogramId: string): Promise<ApiResponse<void>> {
    try {
      if (StoreGroupService.useMock && WHOLE_USE_MOCK) {
        

        return createSuccessResponse(null);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.STORE_GROUP.SET_AS_MAIN_VERSION, {planogramId});
        return response.data;
      }
    } catch (error) {
      console.error('Error setting main version:', error);
      return createErrorResponse(error.message);
    }
  }

  /**
   * Approve planogram
   */
  public async approvePlanogram(planogramId: string): Promise<ApiResponse<Planogram>> {
    try {
      if (StoreGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(500);

        const index = this.planograms.findIndex(p => p.id === planogramId);
        if (index === -1) {
          return createErrorResponse('陈列图不存在', 'NOT_FOUND');
        }

        // Update status to Approved
        this.planograms[index] = {
          ...this.planograms[index],
          status: '审核完毕',
          reviewedAt: new Date().toISOString(),
          reviewedBy: 'current_user', // 实际应该从当前用户获取
          updatedAt: new Date().toISOString(),
        };

        return createSuccessResponse(this.planograms[index]);
      } else {
        // 调用真实 API
        const response = await httpClient.post(`/api/v1/planograms/${planogramId}/approve`);
        return response.data;
      }
    } catch (error) {
      console.error('Error approving planogram:', error);
      return createErrorResponse(error.message);
    }
  }

  /**
   * Reject planogram
   */
  public async rejectPlanogram(planogramId: string, reviewNotes?: string): Promise<ApiResponse<Planogram>> {
    try {
      if (StoreGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(500);

        const index = this.planograms.findIndex(p => p.id === planogramId);
        if (index === -1) {
          return createErrorResponse('陈列图不存在', 'NOT_FOUND');
        }

        // Update status to Rejected
        this.planograms[index] = {
          ...this.planograms[index],
          status: '草稿', // 被拒绝的记录回到草稿状态
          reviewedAt: new Date().toISOString(),
          reviewedBy: 'current_user', // 实际应该从当前用户获取
          reviewNotes: reviewNotes || '审核未通过',
          updatedAt: new Date().toISOString(),
        };

        return createSuccessResponse(this.planograms[index]);
      } else {
        // 调用真实 API
        const response = await httpClient.post(`/api/v1/planograms/${planogramId}/reject`, {
          reviewNotes
        });
        return response.data;
      }
    } catch (error) {
      console.error('Error rejecting planogram:', error);
      return createErrorResponse(error.message);
    }
  }
}

// Export singleton instance
export default StoreGroupService.getInstance();